import { canBuyGiftCard } from "utils/giftCardAvailability";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "elements/button/Button";
import { COLORS } from "models/colors";
import { VARIANTS } from "models/variants";
import { useCompanyQuery } from "routes/company/Company.generated";
import { Breadcrumbs } from "components/breadcrumbs/Breadcrumbs";
import { GiftCardPurchase } from "components/gift-card-purchase/GiftCardPurchase";
import { useCognito } from "providers/cognito";
import { hasPendingGiftCardPurchase } from "utils/giftCardPurchaseRequest";

import "./GiftCard.scss";

// The standalone buy-a-gift-card page.
//
// The normal way in is the modal on the salon page. This page exists so a
// shared or bookmarked link still lands somewhere sensible, and it renders the
// exact same form component so the two cannot drift apart.

export function GiftCard() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useCognito();

  const { data, loading } = useCompanyQuery({
    variables: { slug },
    skip: !slug
  });

  const company = data?.company;
  const onlineSalesEnabled = canBuyGiftCard(company);
  const hasPendingPurchase = hasPendingGiftCardPurchase(user?.id, company?.id);

  if (loading) {
    return <div className="gift-card__loading">Loading…</div>;
  }

  // The salon may have switched gift cards off since the link was shared, so
  // it is checked here rather than assumed.
  if (!company || (!onlineSalesEnabled && !hasPendingPurchase)) {
    return (
      <div className="gift-card">
        <h1 className="gift-card__title">Gift cards are not available</h1>
        <p className="gift-card__lede">
          This salon is not selling gift cards online at the moment.
        </p>
        <Button
          color={COLORS.PRIMARY}
          variant={VARIANTS.OUTLINED}
          text="Back to the salon"
          onClick={() => navigate(`/company/${slug}`)}
        />
      </div>
    );
  }

  return (
    <div className="gift-card">
      <Breadcrumbs rootCrumbName={company.name || "Salon"} breadcrumbs={[]} />

      <h1 className="gift-card__title">Buy a gift card</h1>
      <p className="gift-card__lede">for {company.name}</p>

      <GiftCardPurchase
        companyId={company.id}
        companyName={company.name}
        userPaysFees={company.user_pays_fees}
        recoveryOnly={!onlineSalesEnabled}
        onDone={() => navigate(`/company/${slug}`)}
      />
    </div>
  );
}

export default GiftCard;
